import { clientWithApiClient } from "../../client";
import { appendRegionIds } from "../regions/params";
import type { ApiClient } from "../../types";
import type { PaginatedListResponse, Response } from "../../types";
import type { ChargeQuotePayload, ChargeQuoteResponse, Quote } from "./types";
import { toDateString } from "../../date";
import type { WithBookingWarnings } from "../booking-requests/types";
import type { Rental } from "../rentals/types";

/**
 * Query parameters for filtering quotes
 */
interface QueryParams {
  /**
   * Narrow the list to these regions. `undefined` and `[]` both mean "every
   * region the caller is allowed", and send nothing at all.
   *
   * Explicit here, unlike most lists in this app: the browser api client
   * scopes each GET it builds to the selected region, but this list is
   * rendered on the SERVER, where there is no tab whose selection could be
   * read.
   */
  region_ids?: string[];
  /** Filter quotes by client name */
  clientName?: string;
  /** Filter quotes by start date (ISO string format) */
  startDate?: string;
  /** Filter quotes by end date (ISO string format) */
  endDate?: string;
  /** Filter quotes by status (draft, sent, accepted, expired, cancelled) */
  status?: string;
  /** Page number (default: 1) */
  page?: number;
  /** Items per page (default: 20) */
  pageSize?: number;
}

/**
 * Retrieves a paginated list of quotes from a specific fleet
 *
 * The fleetId is crucial as it acts as a resource filter - quotes are scoped to their fleet,
 * ensuring users can only access quotes that belong to their authorized fleet.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the quotes. This is required for resource filtering
 * @param params - Optional parameters to filter the results
 * @returns Promise that resolves to a paginated list of quotes
 *
 * @example
 * ```typescript
 * const { data: quotes } = await quotes.list(apiClient, "fleet-123", {
 *   clientName: "John Doe",
 *   startDate: "2024-01-01",
 *   endDate: "2024-12-31"
 * });
 * ```
 */
const list = (apiClient: ApiClient, fleetId: string, params?: QueryParams) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  // Region scope. Emits nothing for an absent or empty selection, so a fleet
  // without regions sends exactly the request it sends today.
  appendRegionIds(searchParams, params?.region_ids);

  if (params?.clientName) {
    searchParams.append("client_name", params?.clientName);
  }

  if (params?.startDate) {
    searchParams.append("start_date", params.startDate);
  }

  if (params?.endDate) {
    searchParams.append("end_date", params.endDate);
  }

  if (params?.status) {
    searchParams.append("status", params.status);
  }

  if (params?.page) {
    searchParams.append("page", String(params.page));
  }

  if (params?.pageSize) {
    searchParams.append("page_size", String(params.pageSize));
  }

  const queryString = searchParams.toString();
  const url = queryString ? `/quotes?${queryString}` : "/quotes";

  return clientWithApiClient<PaginatedListResponse<Quote>>(apiClient, url);
};

/**
 * Retrieves a specific quote by its ID within a fleet
 *
 * The fleetId ensures that users can only access quotes that belong to their authorized fleet,
 * providing security and data isolation between different fleets.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the quote. Required for resource access control
 * @param id - Unique identifier of the quote
 * @returns Promise that resolves to the requested quote
 *
 * @throws {Error} If the quote doesn't exist or doesn't belong to the specified fleet
 *
 * @example
 * ```typescript
 * const { data: quote } = await quotes.get(apiClient, "fleet-123", "quote-456");
 * ```
 */
const get = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();

  const url = queryString ? `/quotes/${id}?${queryString}` : `/quotes/${id}`;

  return clientWithApiClient<Response<Quote>>(apiClient, url);
};

/**
 * Creates a new quote in the specified fleet
 *
 * The fleetId determines which fleet the quote will belong to. This is essential for
 * resource organization and access control, as all quotes must be associated with a fleet.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID where the quote will be created. Required for resource association
 * @param quote - Quote data to create (without auto-generated fields like id, etc.)
 * @returns Promise that resolves to the created quote with its assigned ID and metadata
 *
 * @throws {Error} If the quote data is invalid or the fleet doesn't exist
 *
 * @example
 * ```typescript
 * const { data: newQuote } = await quotes.create(apiClient, "fleet-123", {
 *   adults: 2,
 *   children: 1,
 *   infants: 0,
 *   pets: 0,
 *   pick_up: "2024-06-01",
 *   return: "2024-06-05",
 *   client_name: "John Doe",
 *   client_email: "john@example.com",
 *   client_phone: "+1234567890",
 *   notes: "Special requirements for the trip",
 *   daily_rates: [
 *     {
 *       date: "2024-06-01",
 *       quoted_amount: "120.00",
 *       rate_plan_id: "rate-plan-uuid",
 *       notes: "Day 1"
 *     }
 *   ]
 * });
 * ```
 */
const create = (
  apiClient: ApiClient,
  fleetId: string,
  quote: Omit<
    Quote,
    "id" | "inserted_at" | "updated_at" | "status" | "fleet_id"
  >,
) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString ? `/quotes?${queryString}` : "/quotes";

  // Prepare the quote data for the backend
  const quotePayload = {
    ...quote,
    // Convert dates to the correct format for the backend
    pick_up:
      quote.pick_up instanceof Date
        ? toDateString(quote.pick_up)
        : quote.pick_up,
    return:
      quote.return instanceof Date ? toDateString(quote.return) : quote.return,
    // Ensure delivery_options is properly structured if present
    ...(quote.delivery_options && {
      delivery_options: quote.delivery_options,
    }),
  };

  return clientWithApiClient<Response<Quote>>(apiClient, url, {
    method: "POST",
    body: JSON.stringify({ quote: quotePayload }),
  });
};

/**
 * Updates an existing quote
 *
 * The fleetId ensures that the update operation is performed within the correct fleet context,
 * maintaining data integrity and access control.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the quote. Required for resource access control
 * @param id - Unique identifier of the quote to update
 * @param quote - Updated quote data (partial, without id and fleet_id)
 * @returns Promise that resolves to the updated quote
 *
 * @throws {Error} If the quote doesn't exist or the data is invalid
 *
 * @example
 * ```typescript
 * const { data: updatedQuote } = await quotes.update(apiClient, "fleet-123", "quote-456", {
 *   notes: "Updated with final pricing",
 *   daily_rates: [
 *     {
 *       date: "2024-06-01",
 *       quoted_amount: "150.00",
 *       rate_plan_id: "rate-plan-uuid",
 *       notes: "Updated Day 1"
 *     }
 *   ]
 * });
 * ```
 */
const update = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  quote: Omit<Partial<Quote>, "id" | "fleet_id">,
) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString ? `/quotes/${id}?${queryString}` : `/quotes/${id}`;

  // Prepare the quote data for the backend
  const quotePayload = {
    ...quote,
    // Convert dates to the correct format for the backend if they exist
    ...(quote.pick_up && {
      pick_up:
        quote.pick_up instanceof Date
          ? toDateString(quote.pick_up)
          : quote.pick_up,
    }),
    ...(quote.return && {
      return:
        quote.return instanceof Date
          ? toDateString(quote.return)
          : quote.return,
    }),
  };

  return clientWithApiClient<Response<Quote>>(apiClient, url, {
    method: "PATCH",
    body: JSON.stringify({ quote: quotePayload }),
  });
};

/**
 * Deletes a quote from the fleet
 *
 * The fleetId ensures that the deletion operation is performed within the correct fleet context,
 * preventing unauthorized deletions across fleets.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the quote. Required for resource access control
 * @param id - Unique identifier of the quote to delete
 * @returns Promise that resolves when the quote has been successfully deleted
 *
 * @throws {Error} If the quote doesn't exist or cannot be deleted
 *
 * @example
 * ```typescript
 * await quotes.delete(apiClient, "fleet-123", "quote-456");
 * ```
 */
const remove = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString ? `/quotes/${id}?${queryString}` : `/quotes/${id}`;

  return clientWithApiClient(apiClient, url, {
    method: "DELETE",
  });
};

/**
 * Creates a booking (rental) for a quote
 *
 * The fleetId ensures that the booking creation is performed within the correct fleet context,
 * maintaining data integrity and access control.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the quote. Required for resource access control
 * @param id - Unique identifier of the quote to create booking for
 * @param options - `onHold: true` creates the reservation On hold (the customer
 * pays outside FleetSync): its dates are blocked and no confirmation goes out.
 * Nothing is sent for a normal booking.
 * @returns Promise that resolves to the created rental (`data.id` is the
 * rental's public id, the one used in `/reservations/:id`), plus `warnings`
 * when the new booking overlaps a pending booking request
 *
 * @throws {Error} If the quote doesn't exist or booking cannot be created
 *
 * @example
 * ```typescript
 * const { data: rental } = await quotes.createBooking(apiClient, "fleet-123", "quote-456");
 * ```
 */
const createBooking = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  options: { onHold?: boolean } = {},
) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/quotes/${id}/rental?${queryString}`
    : `/quotes/${id}/rental`;

  return clientWithApiClient<WithBookingWarnings<Response<Rental>>>(
    apiClient,
    url,
    {
      method: "POST",
      ...(options.onHold === true
        ? { body: JSON.stringify({ on_hold: true }) }
        : {}),
    },
  );
};

/**
 * `acknowledge_active_holds` goes on the wire only when it is `true`, so a
 * normal charge sends exactly the body it always did.
 */
const buildChargeBody = ({
  acknowledge_active_holds,
  ...rest
}: ChargeQuotePayload) => ({
  ...rest,
  ...(acknowledge_active_holds === true
    ? { acknowledge_active_holds: true }
    : {}),
});

/**
 * Charges the full quote amount to a saved card (off-session) and creates the
 * rental synchronously
 *
 * On success (201) the rental is created and the payment attempted with the
 * saved card. If Stripe requires customer authentication, the response carries
 * `requires_action: true` and the rental stays in `pending_payment` — the
 * customer must complete the payment themselves (e.g. via a resent quote).
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the quote. Required for resource access control
 * @param id - Unique identifier of the quote to charge
 * @param payload - Saved payment method id and a fresh idempotency key (UUID per attempt)
 * @returns Promise that resolves to the created rental and payment
 *
 * @throws {ApiError} 422 with `{code, decline_code, message}` on a Stripe card decline
 * @throws {ApiError} 409 with `code: "active_checkout_holds"` and
 *   `payload.holds` (`ActiveCheckoutHold[]`) when a customer is paying for the
 *   only vehicles still free; resend with `acknowledge_active_holds: true`
 *   once the staff confirmed
 *
 * @example
 * ```typescript
 * const result = await quotes.chargeQuote(apiClient, "fleet-123", "quote-456", {
 *   payment_method_id: "pm-public-id",
 *   idempotency_key: crypto.randomUUID(),
 * });
 * ```
 */
const chargeQuote = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  payload: ChargeQuotePayload,
) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/quotes/${id}/charge?${queryString}`
    : `/quotes/${id}/charge`;

  return clientWithApiClient<{ data: ChargeQuoteResponse }>(apiClient, url, {
    method: "POST",
    body: JSON.stringify({ charge: buildChargeBody(payload) }),
  }).then((response) => response.data);
};

/**
 * Sends a quote to the customer via email
 *
 * Updates the quote status to "sent" and sets the sent_at timestamp.
 * If no expired_at is set, automatically sets expiration to 7 days from now.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the quote. Required for resource access control
 * @param id - Unique identifier of the quote to send
 * @returns Promise that resolves to the updated quote with sent status
 *
 * @throws {Error} If the quote is expired, already accepted, or email sending fails
 *
 * @example
 * ```typescript
 * const { data: sentQuote } = await quotes.sendQuote(apiClient, "fleet-123", "quote-456");
 * ```
 */
const sendQuote = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/quotes/${id}/send?${queryString}`
    : `/quotes/${id}/send`;

  return clientWithApiClient<Response<Quote>>(apiClient, url, {
    method: "POST",
  });
};

/**
 * Accepts a quote (marks it as accepted by the customer)
 *
 * Updates the quote status to "accepted" and sets the accepted_at timestamp.
 * This simulates the customer acceptance action.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the quote. Required for resource access control
 * @param id - Unique identifier of the quote to accept
 * @returns Promise that resolves to the updated quote with accepted status
 *
 * @throws {Error} If the quote is expired or already accepted
 *
 * @example
 * ```typescript
 * const { data: acceptedQuote } = await quotes.acceptQuote(apiClient, "fleet-123", "quote-456");
 * ```
 */
const acceptQuote = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/quotes/${id}/accept?${queryString}`
    : `/quotes/${id}/accept`;

  return clientWithApiClient<Response<Quote>>(apiClient, url, {
    method: "POST",
  });
};

/**
 * Cancels a quote manually (marks it as expired by setting expired_at to now)
 *
 * Updates the quote status to "expired". Only valid for draft or sent quotes.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the quote. Required for resource access control
 * @param id - Unique identifier of the quote to cancel
 * @returns Promise that resolves to the updated quote with expired status
 *
 * @throws {Error} If the quote is already accepted or already cancelled
 *
 * @example
 * ```typescript
 * const { data: cancelledQuote } = await quotes.cancelQuote(apiClient, "fleet-123", "quote-456");
 * ```
 */
const cancelQuote = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/quotes/${id}/cancel?${queryString}`
    : `/quotes/${id}/cancel`;

  return clientWithApiClient<Response<Quote>>(apiClient, url, {
    method: "POST",
  });
};

/**
 * Calculates pricing for a quote preview without creating it
 *
 * This endpoint allows calculating prices before creating a quote, useful for
 * showing the user a pricing breakdown in real-time as they configure their quote.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID for resource access control
 * @param pricingRequest - Pricing request with vehicle, dates, addons, and delivery options
 * @returns Promise that resolves to the pricing breakdown
 *
 * @throws {Error} If the vehicle doesn't exist, dates are invalid, or addons are invalid
 *
 * @example
 * ```typescript
 * const breakdown = await quotes.calculatePricing(apiClient, "fleet-123", {
 *   quotable_type: "fleet_vehicle",
 *   quotable_id: "vehicle-uuid",
 *   pick_up: "2024-01-15",
 *   return: "2024-01-20",
 *   selected_addon_ids: ["addon-uuid-1"],
 *   delivery_options: {
 *     delivery_type: "delivery",
 *     address_type: "custom",
 *     delivery_distance: 25.5
 *   }
 * });
 * ```
 */
const calculatePricing = async (
  apiClient: ApiClient,
  fleetId: string,
  pricingRequest: import("./types").PricingRequest,
) => {
  // `quote_id` makes the server price the quote's saved discount in. The
  // server rejects it (403) for API-key callers, so never send it from a
  // client that authenticates that way. Same precedence as `clientWithApiClient`:
  // a bearer token wins, the API key is the fallback.
  const { quote_id: _quoteId, ...requestWithoutQuoteId } = pricingRequest;
  const usesApiKey = !!apiClient.apiKey && !(await apiClient.getAuthToken?.());
  const requestBody = usesApiKey ? requestWithoutQuoteId : pricingRequest;

  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/quotes/calculate-pricing?${queryString}`
    : "/quotes/calculate-pricing";

  return clientWithApiClient<Response<import("./types").PricingBreakdown>>(
    apiClient,
    url,
    {
      method: "POST",
      body: JSON.stringify({ pricing_request: requestBody }),
    },
  );
};

/**
 * Gets the pricing breakdown for an existing quote
 *
 * Retrieves the saved pricing breakdown from the quote's order.
 * Does not recalculate - returns the data as it was saved.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the quote
 * @param quoteId - Unique identifier of the quote
 * @returns Promise that resolves to the pricing breakdown
 *
 * @throws {Error} If the quote doesn't exist or has no associated order
 *
 * @example
 * ```typescript
 * const breakdown = await quotes.getPricingBreakdown(apiClient, "fleet-123", "quote-456");
 * ```
 */
const getPricingBreakdown = (
  apiClient: ApiClient,
  fleetId: string,
  quoteId: string,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/quotes/${quoteId}/pricing-breakdown?${queryString}`
    : `/quotes/${quoteId}/pricing-breakdown`;

  return clientWithApiClient<Response<import("./types").PricingBreakdown>>(
    apiClient,
    url,
  );
};

/**
 * Quotes API for fleet management
 *
 * Provides complete CRUD operations for quote management within fleets.
 * All operations are scoped by fleetId to ensure proper resource isolation and access control.
 *
 */
export const quotes = {
  list,
  get,
  create,
  update,
  delete: remove,
  createBooking,
  chargeQuote,
  sendQuote,
  acceptQuote,
  cancelQuote,
  calculatePricing,
  getPricingBreakdown,
};
